import { z } from "zod";
/**
 * Business-view projection on a graph node — kept strictly separate from the
 * technical fields below, per the two-view service map contract. Each entry
 * carries a `source` discriminator (`codeowners` / `catalog-info` /
 * `k8s-label` / `k8s-annotation`) so conflicts between sources stay visible
 * in the UI rather than being silently merged.
 */
export const RepoGraphOwner = z.object({
    identifier: z.string(),
    /** "team" | "user" | "group" | "unknown" */
    kind: z.string(),
    /** Glob this ownership applies to. Absent = whole repo. */
    pathGlob: z.string().optional(),
    source: z.string(),
});
const SourcedValue = z.object({ value: z.string(), source: z.string() });
const SourcedSummary = z.object({ summary: z.string(), source: z.string() });
export const RepoGraphBusiness = z.object({
    owners: z.array(RepoGraphOwner),
    systems: z.array(SourcedValue),
    domains: z.array(SourcedValue),
    components: z.array(SourcedValue),
    lifecycle: z.array(SourcedValue),
    tier: z.array(SourcedValue),
    capabilities: z.array(SourcedSummary),
});
/** A node in the service map — one per service (a polyrepo has a single
 *  whole-repo service; a monorepo has one per detected service). */
export const RepoGraphNode = z.object({
    slug: z.string(),
    name: z.string(),
    /** Team that owns this repo, when known. */
    teamName: z.string().nullable(),
    /** Primary language (e.g. "typescript", "go"). */
    language: z.string().nullable(),
    /** Deployment kind: "kubernetes" | "docker-compose" | "dockerfile" | null. */
    deploymentKind: z.string().nullable(),
    /** Container ports the repo exposes, sorted ascending. */
    exposedPorts: z.array(z.number().int().nonnegative()),
    /** Number of identifiers (producer + consumer) — cheap signal of how richly profiled this service is. */
    identifierCount: z.number().int().nonnegative(),
    /** Number of tickets linked to this service (via component → service slug). */
    ticketCount: z.number().int().nonnegative(),
    /** Whether the repo has been ingested (has a manifest). Unmanifested repos still appear as nodes for completeness. */
    hasManifest: z.boolean(),
    /**
     * Business-view projection. `null` when the repo has no manifest or no
     * business extractors fired. When present, every array may still be empty —
     * the UI renders the "Ownership & business" panel only if at least one
     * array has entries.
     */
    business: RepoGraphBusiness.nullable(),
});
/** An edge in the service map — one per reconciled cross-repo dependency. */
export const RepoGraphEdge = z.object({
    sourceSlug: z.string(),
    targetSlug: z.string(),
    /** Edge type: "http" | "rpc" | "event" | "library" | "shared-db". */
    kind: z.string(),
    /** "high" | "medium" | "low". */
    confidence: z.string(),
    /** Identifier kind that produced the match (e.g. "internalPackage", "protoPackage"). */
    signalKind: z.string(),
    /** The matched value (e.g. "@oracus/shared"). */
    signalValue: z.string(),
});
export const RepoGraphResponse = z.object({
    nodes: z.array(RepoGraphNode),
    edges: z.array(RepoGraphEdge),
});
//# sourceMappingURL=repo-graph.js.map